import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Send, 
  User, 
  Clock, 
  Trash2, 
  Edit2,
  X,
  Check,
  MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { apiGet, apiPost, apiPut, apiDelete, ApiError } from '../lib/apiClient';

const AnalysisComments = ({ analysisId, currentUser }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    fetchComments();
  }, [analysisId]);

  const fetchComments = async () => {
    try {
      const data = await apiGet(`/api/analyses/${analysisId}/comments`);
      setComments(data?.comments || []);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const data = await apiPost(`/api/analyses/${analysisId}/comments`, { text: newComment.trim() });
      setComments([data.comment, ...comments]);
      setNewComment('');
      toast.success('Comment added');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to add comment';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (commentId) => {
    if (!editText.trim()) return;

    try {
      await apiPut(`/api/analyses/${analysisId}/comments/${commentId}`, { text: editText.trim() });
      setComments(comments.map(c =>
        c.comment_id === commentId
          ? { ...c, text: editText.trim(), updated_at: new Date().toISOString() }
          : c
      ));
      setEditingId(null);
      setEditText('');
      toast.success('Comment updated');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to update comment';
      toast.error(msg);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await apiDelete(`/api/analyses/${analysisId}/comments/${commentId}`);
      setComments(comments.filter(c => c.comment_id !== commentId));
      toast.success('Comment deleted');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to delete comment';
      toast.error(msg);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const canModify = (comment) => {
    return comment.user_id === currentUser?.user_id || 
           currentUser?.team_role === 'owner' || 
           currentUser?.team_role === 'admin';
  };

  return (
    <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
      <div className="p-4 border-b bg-slate-50">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-slate-600" />
          <h3 className="font-semibold text-slate-900">Team Comments</h3>
          <span className="text-sm text-slate-500">({comments.length})</span>
        </div>
      </div>
      
      <div className="p-4">
        {/* Add Comment Form */}
        <form onSubmit={handleSubmit} className="mb-6">
          <Textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment for your team..."
            className="mb-3 resize-none"
            rows={3}
            data-testid="comment-input"
          />
          <div className="flex justify-end">
            <Button 
              type="submit" 
              disabled={submitting || !newComment.trim()}
              className="gap-2"
              data-testid="submit-comment-btn"
            >
              <Send className="w-4 h-4" />
              {submitting ? 'Posting...' : 'Post Comment'}
            </Button>
          </div>
        </form>

        {/* Comments List */}
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-3 border-slate-200 border-t-slate-600 rounded-full animate-spin" />
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <MessageSquare className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p>No comments yet. Start the conversation!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => (
              <div 
                key={comment.comment_id}
                className="group bg-slate-50 rounded-xl p-4"
                data-testid={`comment-${comment.comment_id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{comment.user_name}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Clock className="w-3 h-3" />
                        <span>{formatDate(comment.created_at)}</span>
                        {comment.updated_at && (
                          <span className="text-slate-400">(edited)</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {canModify(comment) && editingId !== comment.comment_id && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 hover:bg-slate-200 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                          <MoreVertical className="w-4 h-4 text-slate-400" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {comment.user_id === currentUser?.user_id && (
                          <DropdownMenuItem onClick={() => {
                            setEditingId(comment.comment_id);
                            setEditText(comment.text);
                          }}>
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => handleDelete(comment.comment_id)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                
                {editingId === comment.comment_id ? (
                  <div className="mt-2">
                    <Textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="mb-2 resize-none"
                      rows={2}
                    />
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setEditingId(null);
                          setEditText('');
                        }}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Cancel
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => handleUpdate(comment.comment_id)}
                        disabled={!editText.trim()}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-700 text-sm whitespace-pre-wrap">{comment.text}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisComments;
